/**
 * DISPATCH & RETURN — ONE SCREEN, SCAN FIRST (CTS1, Movements — the owner's
 * 18 Sep instruction: learn from the old pages). The operator picks a
 * direction and a customer, then scans; the system works out whether the
 * serial is ours or the customer's own vessel and says so before Add is
 * pressed. The queue fills on the right, the boarding-pass line says what is
 * about to happen in one sentence, and the submit hands a PENDING challan to
 * the approvals worklist. Nothing moves here — it never did in CTS1 either.
 *
 * What CTS2 adds and keeps: the ORDER (ADR-012). An outgoing load is put
 * against an approved order when there is one; without one, the operator
 * says which exception it is and why, in writing (DEF-106). Traded gas is
 * not modelled yet — the third badge slots in when it is.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, ScanBox, Seg, Select, Serial, Text, TextArea } from '../ui.js';
import { Page } from '../shell.js';
import { ApprovalsPanel } from './approvals.js';
import { ChallansPanel, PapersPanel, CustomerPapersPanel } from './challans.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, qty, qtyByUnit, today, words } from '../format.js';
import { screenById, SCREENS } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('move')!.messages;

type Dir = 'out' | 'in';
interface QueueItem {
  cylinderId: number; internalId: string; serialNo: string; gasService: string;
  ownerKind: string; orderLineId?: number;
  outcome: 'ok' | 'leaking' | 'valve_damaged' | 'body_damage' | 'not_accepted'
    | 'fire_exposure' | 'unknown_contents' | 'other_safety_defect' | 'cosmetic_damage';
  note?: string;
}
interface Draft {
  dir: Dir; customerId: string; siteId: string; orderId: string;
  woReason: string; woNote: string; vehicle: string; mode: 'our_truck' | 'customer_pickup';
  queue: QueueItem[];
}

function draftKey(actorId: number): string { return `cts2_move_draft_${actorId}`; }
function readDraft(actorId: number): Draft | null {
  try {
    const raw = globalThis.localStorage?.getItem(draftKey(actorId));
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    return Array.isArray(d.queue) && d.queue.length > 0 ? d : null;
  } catch { return null; }
}

type Tab = 'out' | 'in' | 'waiting' | 'register' | 'papers' | 'customer';

/**
 * 10 Oct — MOVEMENTS. One place for the whole of out-and-in: scan a load out, receive
 * empties in, check what waits, and the one register. Six screens used to do this, two
 * of them the same form twice (docs/CTS2-UX-REDESIGN-2026-10-10.md, A1).
 */
export function Move({ session, tab: initialTab, titleSuffix }: {
  session: Session; tab?: Tab;
  /** 11 Oct — the rail's own name for the tab it sent you to, so the heading matches the thing you clicked. */
  titleSuffix?: string;
}) {
  const heading = titleSuffix ? `Dispatch & Return — ${titleSuffix}` : 'Dispatch & Return';
  const [tab, setTab] = useState<Tab>(() => {
    const t = initialTab ?? routeQuery('tab');
    if (t === 'waiting' || t === 'register' || t === 'papers' || t === 'in' || t === 'out') return t;
    return routeQuery('dir') === 'in' ? 'in' : 'out';
  });
  // the Waiting tab is the approvals worklist; it needs that screen's function (looked up by id, not by
  // the screenById literal — the structure guard reads that literal as this file's own contract)
  // #/approvals and #/challans reuse this component: when the route's tab changes, follow it
  useEffect(() => {
    const t = initialTab ?? routeQuery('tab');
    if (t === 'waiting' || t === 'register' || t === 'papers' || t === 'customer' || t === 'in' || t === 'out') setTab(t);
    else if (initialTab === undefined) setTab(routeQuery('dir') === 'in' ? 'in' : 'out');
  }, [initialTab, typeof location === 'undefined' ? '' : location.hash]);
  const mayCheck = can(session, SCREENS.find((x) => x.id === 'approvals')?.requires ?? 'view.dispatch');
  const tabs = (
    <div className="toolbar" style={{ marginBottom: 14 }}>
      <Seg name="movements-tab" value={tab} onChange={(v) => { const t = v as Tab; setTab(t); if (t === 'in' || t === 'out') { /* the form keeps its own direction */ } }}
           options={[{ value: 'out', label: 'Send out' }, { value: 'in', label: 'Receive in' },
                     ...(mayCheck ? [{ value: 'waiting', label: 'Waiting for a check' }] : []),
                     { value: 'register', label: 'Register' }, { value: 'papers', label: 'Papers' },
                     // 14 Oct — the owner: a tab of its own, so a customer's own cylinders are
                     // never read as ours going out on rental.
                     { value: 'customer', label: 'Customer challans' }]} />
    </div>
  );
  if (tab === 'waiting') {
    return <Page title={heading} sub="Out and in — scan, check, and the register of every challan.">{tabs}<ApprovalsPanel session={session} /></Page>;
  }
  if (tab === 'customer') {
    return <Page title={heading} sub="Their cylinders: received for filling, and sent home again. Job work, never rental.">{tabs}<CustomerPapersPanel /></Page>;
  }
  if (tab === 'papers') {
    return <Page title={heading} sub="Out and in — scan, check, the register of every challan, and every paper issued.">{tabs}<PapersPanel /></Page>;
  }
  if (tab === 'register') {
    return <Page title={heading} sub="Out and in — scan, check, and the register of every challan.">{tabs}<ChallansPanel id={null} session={session} /></Page>;
  }
  return <MoveForm session={session} dir={tab} tabs={tabs} onDir={(d) => setTab(d)} />;
}

function MoveForm({ session, dir: dirIn, tabs, onDir }: { session: Session; dir: 'in' | 'out'; tabs: React.ReactNode; onDir: (d: 'in' | 'out') => void }) {
  // Home sends the yard here with the direction chosen (#/move?dir=in)
  const [dir, setDir] = useState<Dir>(dirIn);
  useEffect(() => { setDir(dirIn); }, [dirIn]);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [siteId, setSiteId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [woReason, setWoReason] = useState('');
  const [woNote, setWoNote] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [receivedOn, setReceivedOn] = useState(today());   // 10 Oct (QA ISSUE-010) — a return records the day it came back, not the day it was typed
  const [mode, setMode] = useState<'our_truck' | 'customer_pickup'>('our_truck');
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<Dir | null>(null);
  const [saved, setSaved] = useState<Draft | null>(() => readDraft(session.actorId));
  const [done, setDone] = useState<{ challanId: number; dir: Dir; n: number } | null>(null);
  const runner = useRun(MESSAGES);

  // CTS1's unfinished-work banner: the queue survives a reload, per operator
  useEffect(() => {
    try {
      if (queue.length === 0) { globalThis.localStorage?.removeItem(draftKey(session.actorId)); return; }
      globalThis.localStorage?.setItem(draftKey(session.actorId),
        JSON.stringify({ dir, customerId, siteId, orderId, woReason, woNote, vehicle, mode, queue } satisfies Draft));
    } catch { /* a private window forgets; the screen still works */ }
  }, [dir, customerId, siteId, orderId, woReason, woNote, vehicle, mode, queue, session.actorId]);

  const sites = useAsk(() => (customerId ? api.view('sites.list', { customerId: Number(customerId), active: true }) : Promise.resolve({ rows: [] })), [customerId], MESSAGES);
  const orders = useAsk(() => (dir === 'out' && customerId
    ? api.view('orders.list', { customerId: Number(customerId), status: 'approved', outstanding: true, pageSize: 50 })
    : Promise.resolve({ rows: [], page: { number: 1, size: 0, total: 0 } })), [dir, customerId], MESSAGES);
  const order = useAsk(() => (orderId ? api.view('orders.get', { orderId: Number(orderId) }) : Promise.resolve(null)), [orderId], MESSAGES);

  // the tap-to-add list: what can actually go this way, no typing needed
  const offer = useAsk(() => {
    if (dir === 'out') return api.view('cylinders.search', { state: 'filled_in_plant', ownerKind: 'company', pageSize: 40 });
    if (customerId) return api.view('cylinders.search', { withCustomerId: Number(customerId), pageSize: 40 });
    return Promise.resolve(null);
  }, [dir, customerId], MESSAGES);

  // the live preview: from two characters the serial is resolved BEFORE Add
  const preview = useAsk(() => (scan.trim().length >= 2
    ? api.view('cylinders.search', { q: scan.trim(), pageSize: 6 })
    : Promise.resolve(null)), [scan], MESSAGES);

  // when the customer changes, the primary site follows without being asked
  useEffect(() => {
    const rows = sites.data?.rows ?? [];
    if (rows.length && !rows.some((s: { siteId: number }) => String(s.siteId) === siteId)) {
      const primary = rows.find((s: { isPrimary: boolean }) => s.isPrimary) ?? rows[0];
      if (primary) setSiteId(String(primary.siteId));
    }
  }, [sites.data]); // eslint-disable-line

  type Hit = { cylinderId: number; internalId: string; serialNo: string; gasService: string; ownerKind: string; ownerCustomerId: number | null; state: string | null; holder: string | null; openTrip: { customerId: number } | null };

  /** CTS1's routing rule, CTS2's facts: OUT wants filled-in-plant (ours, or the customer's own vessel); IN wants what is out with this customer. */
  function eligibility(h: Hit): { ok: boolean; why?: string } {
    if (queue.some((x) => x.cylinderId === h.cylinderId)) return { ok: false, why: `${h.internalId} is already in the queue.` };
    if (dir === 'out') {
      if (h.state !== 'filled_in_plant') return { ok: false, why: `${h.internalId} is ${words(h.state ?? 'unknown')} — only a filled cylinder standing in the plant can go out.` };
      // the door's own rule (P1): a customer's vessel never travels on a
      // dispatch — it goes home on a return-to-owner document and pays no rent
      if (h.ownerKind === 'customer') {
        return { ok: false, why: `${h.internalId} is the customer's own vessel — send it back with a return-to-owner note, not a dispatch; it never carries rent.` };
      }
      return { ok: true };
    }
    if (!h.openTrip) {
      // 10 Oct (QA ISSUE-007): a customer's own vessel does not come back on a return —
      // it comes IN FOR WORK on a job, from the customer's page, where the fill is charged
      return { ok: false, why: h.ownerKind === 'customer'
        ? `${h.internalId} is the customer's own cylinder — it comes in for filling on a job from the customer's page (Customers › their page › Take cylinders in for filling), not on a return.`
        : `${h.internalId} is ${words(h.state ?? 'unknown')} — it is not out on a trip, so there is nothing to receive.` };
    }
    if (customerId && h.openTrip.customerId !== Number(customerId)) {
      return { ok: false, why: `${h.internalId} is out with customer #${h.openTrip.customerId}, not the one chosen — returns follow the cylinder's real holder.` };
    }
    return { ok: true };
  }

  function addHit(h: Hit): void {
    const check = eligibility(h);
    if (!check.ok) { setScanError(check.why ?? 'That cylinder cannot join this queue.'); return; }
    if (dir === 'out' && h.ownerKind === 'customer' && !customerId && h.ownerCustomerId) setCustomerId(String(h.ownerCustomerId));
    const line = dir === 'out' && order.data
      ? order.data.lines.find((l: { gasService: string; qtyOutstanding: number; orderLineId: number }) =>
          l.gasService === h.gasService && l.qtyOutstanding > queue.filter((x) => x.orderLineId === l.orderLineId).length)
      : null;
    setQueue((old) => [...old, {
      cylinderId: h.cylinderId, internalId: h.internalId, serialNo: h.serialNo, gasService: h.gasService,
      ownerKind: h.ownerKind, ...(line ? { orderLineId: line.orderLineId } : {}), outcome: 'ok',
    }]);
    setScan(''); setScanError(null);
  }

  async function addTyped(): Promise<void> {
    const typed = scan.trim();
    if (!typed) return;
    // a barcode gun fires Enter the instant it finishes — the add looks the
    // serial up itself rather than trusting the preview strip to have caught up
    let rows: Hit[];
    try { rows = (await api.view('cylinders.search', { q: typed, pageSize: 6 })).rows as Hit[]; }
    catch (e) { setScanError(e instanceof Error ? e.message : String(e)); return; }
    const exact = rows.filter((r) => r.internalId.toLowerCase() === typed.toLowerCase() || r.serialNo.toLowerCase() === typed.toLowerCase());
    const pool = exact.length ? exact : rows;
    if (pool.length === 0) { setScanError(`No cylinder answers to “${typed}”.`); return; }
    const eligible = pool.filter((r) => eligibility(r).ok);
    if (eligible.length === 1) { addHit(eligible[0]!); return; }
    if (eligible.length > 1) { setScanError(`“${typed}” matches more than one cylinder — tap the right one below.`); return; }
    setScanError(eligibility(pool[0]!).why ?? `“${typed}” cannot join this queue.`);
  }

  function switchDir(next: Dir): void {
    if (next === dir) return;
    if (queue.length > 0) { setSwitching(next); return; }
    setDir(next); setOrderId(''); setScanError(null);
  }

  async function submit() {
    setDone(null);
    const n = queue.length;
    const ok = await runner.run(async (retryKey) => {
      const opts = retryKey ? { idempotencyKey: retryKey } : {};
      if (dir === 'out') {
        const r = await api.command('challan.createDispatch', {
          customerId: Number(customerId), siteId: Number(siteId), eventDate: today(), deliveryMode: mode,
          ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
          ...(orderId ? { orderId: Number(orderId) } : {}),
          ...(!orderId && woReason ? { withoutOrder: { reason: woReason as 'walk_in' | 'replacement' | 'emergency' | 'legacy', note: woNote.trim() } } : {}),
          lines: queue.map((x) => ({ cylinderId: x.cylinderId, ...(x.orderLineId ? { orderLineId: x.orderLineId } : {}) })),
        }, opts);
        const v = r.value as { challanId?: number };
        if (v?.challanId) setDone({ challanId: v.challanId, dir, n });
        return r;
      }
      const r = await api.command('challan.createReturn', {
        customerId: Number(customerId), siteId: Number(siteId), eventDate: receivedOn || today(),
        ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
        lines: queue.map((x) => ({ cylinderId: x.cylinderId, outcome: x.outcome, ...(x.note?.trim() ? { note: x.note.trim() } : {}) })),
      }, opts);
      const v = r.value as { challanId?: number };
      if (v?.challanId) setDone({ challanId: v.challanId, dir, n });
      return r;
    });
    // the next load starts clean: the last customer must not swallow the next scan by accident (audit M1, 22 Sep)
    if (ok) { setQueue([]); setScan(''); setScanError(null); setCustomerId(''); setSiteId(''); setOrderId(''); setWoReason(''); setWoNote(''); setVehicle(''); }
  }

  // the picker hands back the row it chose; no capped list is needed for a name
  const ready = customerId && siteId && queue.length > 0
    && (dir === 'in' || orderId || (woReason && woNote.trim().length >= 10));
  const offerRows: Hit[] = ((offer.data?.rows ?? []) as Hit[]).filter((r) => eligibility(r).ok);
  // the customer's own vessels first, the way CTS1 listed them
  const offerSorted = [...offerRows].sort((a, b) => Number(b.ownerKind === 'customer') - Number(a.ownerKind === 'customer'));

  return (
    <Page title="Dispatch & Return" sub="Scan or type any cylinder — ours or the customer's own vessel is worked out for you. Nothing moves until it is approved.">
      {tabs}
      {saved ? (
        <Banner kind="warn" title={`Unfinished work — ${count(saved.queue.length)} cylinder(s) scanned but not submitted.`}
                detail="Restore the queue to carry on, or discard it." />
      ) : null}
      {saved ? (
        <div className="toolbar">
          <Button kind="primary" onClick={() => {
            setDir(saved.dir); setCustomerId(saved.customerId); setSiteId(saved.siteId); setOrderId(saved.orderId);
            setWoReason(saved.woReason); setWoNote(saved.woNote); setVehicle(saved.vehicle); setMode(saved.mode);
            setQueue(saved.queue); setSaved(null);
            // AND THE CUSTOMER'S NAME WITH HIM. The draft keeps the id, which
            // is what submits, and the name is the screen's own: restoring set
            // the id and left the name empty, so the confirmation line —
            // "Sending 2 cylinders to Brahmaputra Fabricators · truck …" —
            // vanished, and the one sentence that says where a load is going
            // was missing on exactly the loads somebody had come back to.
            if (saved.customerId) {
              void api.view('customers.get', { customerId: Number(saved.customerId) }).then(
                (r) => setCustomerName(r.customer?.name),
                () => { /* the name is the sentence's, not the submission's */ },
              );
            }
          }}>Restore the queue</Button>
          <Button onClick={() => { setSaved(null); try { globalThis.localStorage?.removeItem(draftKey(session.actorId)); } catch { /* gone is gone */ } }}>Discard it</Button>
        </div>
      ) : null}

      {done ? (
        <Banner kind="good" title={`Submitted for approval — ${count(done.n)} cylinder(s) ${done.dir === 'out' ? 'to go out' : 'coming back'}.`}
                detail="Sent for checking — cylinders move only after a reviewer approves. Start the next load below, or open this one." />
      ) : null}
      {done ? (
        <div className="toolbar">
          <Button onClick={() => go(`#/challans/${done.challanId}`)}>Open it on the register</Button>
          {can(session, 'challan.approve') ? <Button onClick={() => go('#/approvals')}>Go to approvals</Button> : null}
        </div>
      ) : null}

      {switching ? (
        <Banner kind="warn" title="Switching between Send and Receive starts a new challan." detail="The queue you have built will be cleared." />
      ) : null}
      {switching ? (
        <div className="toolbar">
          <Button kind="danger" onClick={() => { setDir(switching); setQueue([]); setOrderId(''); setScanError(null); setSwitching(null); }}>Switch and clear the queue</Button>
          <Button onClick={() => setSwitching(null)}>Stay</Button>
        </div>
      ) : null}

      <div className="grid two">
        <Card title="The movement">
          {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

          {/* 11 Oct — the owner: "why are there two receive in?" The tabs above are the one
              direction control; the toggle that used to sit here repeated them. */}
          <div style={{ display: 'none' }}>
            <Seg name="direction" value={dir} onChange={(v) => { switchDir(v as Dir); onDir(v as Dir); }}
                 options={[{ value: 'out', label: 'Sending OUT' }, { value: 'in', label: 'Receiving IN' }]} />
          </div>

          <CustomerPickerById name="customer" label={dir === 'out' ? 'Which customer?' : 'Returning from which customer?'} customerId={customerId}
                              onPick={(v, c) => { setCustomerId(v); setCustomerName(c?.name); setSiteId(''); setOrderId(''); }} placeholder="Search or select customer…" />
          <Select name="site" label="Site" value={siteId} onChange={setSiteId} placeholder={customerId ? 'Choose a site' : 'Choose the customer first'}
                  options={(sites.data?.rows ?? []).map((s: { siteId: number; name: string; isPrimary: boolean }) => ({ value: String(s.siteId), label: s.name + (s.isPrimary ? ' (primary)' : '') }))} />

          {dir === 'out' ? (
            <>
              <Select name="order" label="Order being filled" value={orderId} onChange={setOrderId}
                      placeholder="No order — this is a direct challan"
                      help="a load prepared from an approved order is priced by the order's approved rates"
                      options={(orders.data?.rows ?? []).map((o) =>
                        ({ value: String(o.orderId),
                           label: `#${o.orderId} · ${o.unit ? qty(o.qtyOutstanding, o.unit) : qtyByUnit(o.outstandingByUnit)} outstanding · wanted ${date(o.requestedDate)}` }))} />
              {!orderId ? (
                <>
                  <Select name="without-order" label="Why is there no order?" value={woReason} onChange={setWoReason} placeholder="Choose the reason"
                          options={[{ value: 'walk_in', label: 'Walk-in customer' }, { value: 'replacement', label: 'Replacement' }, { value: 'emergency', label: 'Emergency' }, { value: 'legacy', label: 'Legacy / old register entry' }]} />
                  <TextArea name="without-order-note" label="Say what happened" value={woNote} onChange={setWoNote}
                            help="a few words, at least ten characters — the record of why there was no order" />
                </>
              ) : null}
              <Select name="mode" label="Delivery" value={mode} onChange={(v) => setMode(v === 'customer_pickup' ? 'customer_pickup' : 'our_truck')}
                      options={[{ value: 'our_truck', label: 'Our truck' }, { value: 'customer_pickup', label: 'Customer collects' }]} />
            </>
          ) : null}
          {dir === 'in' ? <Text name="received-on" label="Received on" type="date" value={receivedOn} onChange={setReceivedOn} required help="the day the cylinders came back — holding stops that day" /> : null}
          {dir === 'in' ? (() => {
            // 11 Oct — the owner: "how does one assign where the cylinder is while receiving?"
            // It lands at the yard this session is working at; the picker in the sidebar switches
            // yards when a person holds more than one. Its stage is worked out from what happens
            // to it; a physical row is optional and set on Yard › Rows once it is in.
            const here = session.branches.find((b) => b.branchId === session.activeBranchId) ?? session.branches.find((b) => b.isDefault) ?? session.branches[0] ?? null;
            return (
              <div className="field">
                <label>Received at</label>
                <div style={{ padding: '8px 0' }}><strong>{here ? `${here.name} (${here.code})` : 'the yard you are signed in at'}</strong>
                  <span className="muted"> — {session.branches.length > 1 ? 'switch yards with the picker in the sidebar' : 'your only yard'} · stage is worked out on approval; a row is optional (Yard › Rows)</span></div>
              </div>
            );
          })() : null}
          <Text name="vehicle" label="Truck / vehicle no (optional)" value={vehicle} onChange={setVehicle} placeholder="e.g. WB-26-A-1234" />

          {scanError ? <Banner kind="warn" title={scanError} /> : null}
          <ScanBox label="Scan or type any cylinder" value={scan} onChange={(v) => { setScan(v); setScanError(null); }} onAdd={() => { void addTyped(); }}
                   help="type two characters and the matching cylinders appear below" />

          {scan.trim().length >= 2 && preview.data ? (
            <div style={{ marginBottom: 12 }}>
              {(preview.data.rows as Hit[]).length === 0 ? <Banner kind="warn" title={`No cylinder answers to “${scan.trim()}”.`} /> :
                (preview.data.rows as Hit[]).map((h) => {
                  const check = eligibility(h);
                  return (
                    <div className="queue-item" key={h.cylinderId}>
                      <span className="grow">
                        <Serial>{h.internalId}</Serial> <span className="muted">{h.serialNo} · {words(h.gasService)}</span>{' '}
                        <Pill tone={h.ownerKind === 'customer' ? 'testing' : 'good'}>{h.ownerKind === 'customer' ? 'customer' : 'ours'}</Pill>{' '}
                        {check.ok ? <Pill tone="good">{dir === 'out' ? 'ready to send' : 'out on a trip'}</Pill>
                                  : <span className="muted">{check.why}</span>}
                      </span>
                      {check.ok ? <Button small kind="primary" onClick={() => addHit(h)}>Add</Button> : null}
                    </div>
                  );
                })}
            </div>
          ) : null}

          <Button kind="primary" wide disabled={!ready || runner.busy} onClick={() => { void submit(); }}>
            {runner.busy ? 'Submitting…' : queue.length === 0 ? 'Scan or type a cylinder to begin'
              : `Submit for approval — ${count(queue.length)} cylinder${queue.length === 1 ? '' : 's'}`}
          </Button>
          <p className="muted" style={{ textAlign: 'center', marginBottom: 0 }}>
            {ready && customerName
              ? `${dir === 'out' ? 'Sending' : 'Receiving'} ${count(queue.length)} cylinder${queue.length === 1 ? '' : 's'} ${dir === 'out' ? 'to' : 'back from'} ${customerName}${vehicle.trim() ? ` · truck ${vehicle.trim().toUpperCase()}` : ''}.`
              : 'Cylinders move only after a reviewer approves. Until then they stay where they are.'}
          </p>
        </Card>

        <div>
          <Card title={dir === 'out' ? `Queue · ${count(queue.length)}` : `Coming back · ${count(queue.length)}`}
                {...(queue.length ? { actions: <Button small onClick={() => setQueue([])}>Clear all</Button> } : {})}>
            {queue.length === 0 ? (
              <div className="queue empty">
                <strong>Queue is empty</strong>
                <span>Scan on the left, or tap a cylinder below.<br />Each one you add appears here.</span>
              </div>
            ) : queue.map((x) => (
              <div className="queue-item" key={x.cylinderId}>
                <span className="grow">
                  <Serial>{x.internalId}</Serial> <span className="muted">{x.serialNo} · {words(x.gasService)}</span>{' '}
                  <Pill tone={x.ownerKind === 'customer' ? 'testing' : 'good'}>{x.ownerKind === 'customer' ? 'customer' : 'ours'}</Pill>
                  {x.orderLineId ? <span className="muted"> · line #{x.orderLineId}</span> : null}
                </span>
                {dir === 'in' ? (
                  <select aria-label={`Condition of ${x.internalId}`} value={x.outcome}
                          onChange={(e: { target: { value: string } }) => setQueue((old) => old.map((y) => y.cylinderId === x.cylinderId
                            ? { ...y, outcome: e.target.value as QueueItem['outcome'] } : y))}>
                    <option value="ok">came back sound</option>
                    <optgroup label="Stops the cylinder — it is quarantined on arrival">
                      <option value="leaking">leaking</option>
                      <option value="valve_damaged">valve damaged</option>
                      <option value="body_damage">body damage</option>
                      <option value="fire_exposure">has been in a fire</option>
                      <option value="unknown_contents">contents unknown</option>
                      <option value="other_safety_defect">something else unsafe</option>
                    </optgroup>
                    <optgroup label="Does not stop it">
                      <option value="cosmetic_damage">cosmetic damage only (paint, label)</option>
                      <option value="not_accepted">not accepted</option>
                    </optgroup>
                  </select>
                ) : null}
                <Button small kind="danger" onClick={() => setQueue((old) => old.filter((y) => y.cylinderId !== x.cylinderId))}>Remove</Button>
              </div>
            ))}
          </Card>

          <Card title={dir === 'out' ? `Ready to send (${count(offerSorted.length)}) · tap to add` : customerId ? `Out with ${customerName ?? 'this customer'} (${count(offerSorted.length)}) · tap to return` : 'Out with…'}
                sub={dir === 'out' ? 'ours, filled and standing in the plant — a customer\'s own vessel goes home on a return-to-owner paper' : customerId ? 'what this customer is actually holding' : 'choose the customer to see what they hold'}>
            {offer.loading && !offer.data ? <Loading what="the cylinders" /> :
              offerSorted.length === 0 ? <p className="muted" style={{ margin: 0 }}>{dir === 'out' ? 'Nothing filled is standing in the plant.' : customerId ? 'This customer holds nothing of ours.' : ''}</p> :
              offerSorted.slice(0, 40).map((h) => (
                <div className="queue-item" key={h.cylinderId}>
                  <span className="grow">
                    <Serial>{h.internalId}</Serial> <span className="muted">{h.serialNo} · {words(h.gasService)}</span>{' '}
                    <Pill tone={h.ownerKind === 'customer' ? 'testing' : 'good'}>{h.ownerKind === 'customer' ? 'customer' : 'ours'}</Pill>
                  </span>
                  <Button small onClick={() => addHit(h)}>Add</Button>
                </div>
              ))}
          </Card>
        </div>
      </div>
    </Page>
  );
}
